import { Language } from "../../data/constants";
import { ExamAndExpertAssignmentRequestModel } from "../../model/ExamAssignmentRequestModel";
import { ExamDayViewModel } from "../../model/ExamDayViewModel";
import SendInvitationResponse from "../../model/examSession/SendInvitationResponse";
import { ExamSessionViewModel } from "../../model/ExamSessionViewModel";
import { ExamLocationViewModel } from "../../model/ExamSignup/ModuleExamDetailsViewModel";
import { ExpertAssignmentRequestModel } from "../../model/ExpertAssignmentRequestModel";
import { PersonType } from "../../model/PersonType";
import { RoomAssignmentViewModel } from "../../model/RoomAssignmentViewModel";
import { ApiResponse } from "../CustomFetch";
import { api } from "./BaseApi";

const getExamSessionUrl = (id?: string) => "ExamSession" + (id === undefined ? "" : `/${id}`);

const getExamDayUrl = (examSessionId: string, examDayId?: string) =>
  `${getExamSessionUrl(examSessionId)}/ExamDay` + (examDayId === undefined ? "" : `/${examDayId}`);

const getRoomAssignmentUrl = (examSessionId: string, examDayId: string, roomAssignmentId?: string) =>
  `${getExamSessionUrl(examSessionId)}/examDay/${examDayId}/roomAssignment` +
  (roomAssignmentId === undefined ? "" : `/${roomAssignmentId}`);

const examSessionApi = api.enhanceEndpoints({ addTagTypes: ["ExamSessions", "AvailableDays"] }).injectEndpoints({
  endpoints: (builder) => ({
    getExamSessions: builder.query<ExamSessionViewModel[], void>({
      query: () => getExamSessionUrl(),
      transformResponse: (response: ApiResponse<ExamSessionViewModel[]>) => response.data,
      providesTags: (_result, _error, arg) => [{ type: "ExamSessions", id: "LIST" }],
    }),
    getExamSession: builder.query<ExamSessionViewModel, string>({
      query: getExamSessionUrl,
      transformResponse: (response: ApiResponse<ExamSessionViewModel>) => response.data,
      providesTags: (_result, _error, arg) => [{ type: "ExamSessions", id: arg }],
    }),
    getAvailableDays: builder.query<
      ExamLocationViewModel[],
      { examSessionId: string; examId: string; language: Language }
    >({
      query: ({ examSessionId, examId, language }) =>
        `${getExamSessionUrl(examSessionId)}/exam/${examId}/availableDays?language=${language}`,
      transformResponse: (response: ApiResponse<ExamLocationViewModel[]>) => response.data,
      providesTags: ["AvailableDays"],
    }),
    deleteExamSession: builder.mutation<ApiResponse<void>, string>({
      query: (examSessionId) => ({
        url: getExamSessionUrl(examSessionId),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg },
      ],
    }),
    createExamSession: builder.mutation<ApiResponse<string>, ExamSessionViewModel>({
      query: (examSession: ExamSessionViewModel) => ({
        url: getExamSessionUrl(),
        body: examSession,
        method: "POST",
      }),
      invalidatesTags: [{ type: "ExamSessions", id: "LIST" }],
    }),
    buildExamSession: builder.mutation<ApiResponse<string>, ExamSessionViewModel>({
      query: (examSession: ExamSessionViewModel) => ({
        url: `${getExamSessionUrl()}/build`,
        body: examSession,
        method: "POST",
      }),
      invalidatesTags: [{ type: "ExamSessions", id: "LIST" }],
    }),
    updateExamSession: builder.mutation<ApiResponse<void>, ExamSessionViewModel>({
      query: (examSessionViewModel) => ({
        url: getExamSessionUrl(),
        body: examSessionViewModel,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.id },
      ],
    }),
    updateAndPublishExamSession: builder.mutation<unknown, ExamSessionViewModel>({
      async queryFn(examSessionViewModel, _queryApi, _extraOptions, fetchWithBaseQuery) {
        const updateResponse = await fetchWithBaseQuery({
          url: getExamSessionUrl(),
          body: examSessionViewModel,
          method: "PUT",
        });
        if (updateResponse.error) {
          return updateResponse;
        }

        const publishResponse = await fetchWithBaseQuery({
          url: `${getExamSessionUrl(examSessionViewModel.id)}/publish`,
          body: {},
          method: "POST",
        });
        return publishResponse;
      },
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.id },
      ],
    }),
    unPublishExamSession: builder.mutation<ApiResponse<void>, string>({
      query: (examSessionId) => ({
        url: `${getExamSessionUrl(examSessionId)}/unpublish`,
        body: {},
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg },
      ],
    }),
    deleteExamDay: builder.mutation<ApiResponse<void>, { examSessionId: string; examDayId: string }>({
      query: ({ examSessionId, examDayId }) => ({
        url: getExamDayUrl(examSessionId, examDayId),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "AvailableDays" },
        { type: "ExamSessions", id: arg.examSessionId },
      ],
    }),
    createExamDay: builder.mutation<ApiResponse<void>, { examSessionId: string; data: ExamDayViewModel }>({
      query: ({ examSessionId, data }) => ({
        url: getExamDayUrl(examSessionId),
        body: data,
        headers: { "Content-Type": "application/json" },
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "AvailableDays" },
        { type: "ExamSessions", id: arg.examSessionId },
      ],
    }),
    updateExamDay: builder.mutation<ApiResponse<void>, { examSessionId: string; data: ExamDayViewModel }>({
      query: ({ examSessionId, data }) => ({
        url: getExamDayUrl(examSessionId),
        body: data,
        headers: { "Content-Type": "application/json" },
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "AvailableDays" },
        { type: "ExamSessions", id: arg.examSessionId },
      ],
    }),
    createRoomAssignment: builder.mutation<
      ApiResponse<void>,
      { examSessionId: string; examDayId: string; data: RoomAssignmentViewModel }
    >({
      query: ({ examSessionId, examDayId, data }) => ({
        url: getRoomAssignmentUrl(examSessionId, examDayId),
        body: data,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.examSessionId },
        "AvailableDays",
      ],
    }),
    deleteRoomAssignment: builder.mutation<
      ApiResponse<void>,
      { examSessionId: string; examDayId: string; roomAssignmentId: string }
    >({
      query: ({ examSessionId, examDayId, roomAssignmentId }) => ({
        url: getRoomAssignmentUrl(examSessionId, examDayId, roomAssignmentId),
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.examSessionId },
        "AvailableDays",
      ],
    }),
    updateExamAndExpertAssignments: builder.mutation<
      ApiResponse<void>,
      { examSessionId: string; data: ExamAndExpertAssignmentRequestModel }
    >({
      query: ({ examSessionId, data }) => ({
        url: `${getExamSessionUrl(examSessionId)}/examAndExpertAssignment`,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.examSessionId },
        "AvailableDays",
      ],
    }),
    updateExpertAssignments: builder.mutation<
      ApiResponse<void>,
      { examSessionId: string; examDayId: string; data: ExpertAssignmentRequestModel[] }
    >({
      query: ({ examSessionId, examDayId, data }) => ({
        url: `${getExamSessionUrl(examSessionId)}/ExamDay/${examDayId}/Expert`,
        body: data,
        method: "PUT",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.examSessionId },
        "AvailableDays",
      ],
    }),
    publishExamResults: builder.mutation<ApiResponse<void>, string>({
      query: (examSessionId) => ({
        url: `${getExamSessionUrl(examSessionId)}/publishExamResults`,
        body: {},
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg },
      ],
    }),
    addExamSessionLocations: builder.mutation<ApiResponse<void>, { examSessionId: string; locationIds: string[] }>({
      query: ({ examSessionId, locationIds }) => ({
        url: `${getExamSessionUrl(examSessionId)}/location`,
        body: locationIds,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.examSessionId },
        "AvailableDays",
      ],
    }),
    deleteExamSessionLocation: builder.mutation<ApiResponse<void>, { examSessionId: string; locationId: string }>({
      query: ({ examSessionId, locationId }) => ({
        url: `${getExamSessionUrl(examSessionId)}/location/${locationId}`,
        body: {},
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.examSessionId },
        "AvailableDays",
      ],
    }),
    startSurvey: builder.mutation<ApiResponse<void>, { examSessionId: string; surveyDeadline: Date }>({
      query: ({ examSessionId, surveyDeadline }) => ({
        url: `${getExamSessionUrl(examSessionId)}/survey`,
        body: surveyDeadline,
        method: "POST",
      }),
    }),
    sendInvitation: builder.mutation<
      SendInvitationResponse,
      {
        examSessionId: string;
        locationIds: string[];
        personType: PersonType;
      }
    >({
      query: ({ examSessionId, locationIds, personType: invitationType }) => ({
        url: `${getExamSessionUrl(examSessionId)}/invitation/${invitationType}`,
        body: locationIds,
        method: "POST",
      }),
      transformResponse: (response: ApiResponse<SendInvitationResponse>) => response.data,
      invalidatesTags: (_result, _error, arg) => [
        { type: "ExamSessions", id: "LIST" },
        { type: "ExamSessions", id: arg.examSessionId },
      ],
    }),
    sendResultNotifications: builder.mutation<ApiResponse<void>, string>({
      query: (examSessionId) => ({
        url: `${getExamSessionUrl(examSessionId)}/examResult`,
        body: {},
        method: "POST",
      }),
    }),
    exportOrders: builder.mutation<ApiResponse<void>, string>({
      query: (examSessionId) => ({
        url: `${getExamSessionUrl(examSessionId)}/export`,
        body: {},
        method: "POST",
      }),
    }),
    setCandidatePin: builder.mutation<ApiResponse<void>, { examSessionId: string; candidatePin: string }>({
      query: ({examSessionId, candidatePin}) => ({
        url: `${getExamSessionUrl(examSessionId)}/setCandidatePin`,
        body: { pin: candidatePin},
        method: "POST",
      }),
    }),
    setExpertPin: builder.mutation<ApiResponse<void>, { examSessionId: string; expertPin: string }>({
      query: ({examSessionId, expertPin}) => ({
        url: `${getExamSessionUrl(examSessionId)}/setExpertPin`,
        body: { pin: expertPin },
        method: "POST",
      }),
    }),
  }),
});

export const importPevUrl = `${getExamSessionUrl()}/importPev`;

export const getWrittenExamScoreImportUrl = (examSessionId: string) =>
  `${getExamSessionUrl(examSessionId)}/writtenExamScoreFile`;

export const {
  endpoints,
  useGetExamSessionsQuery,
  useGetExamSessionQuery,
  useGetAvailableDaysQuery,
  useDeleteExamSessionMutation,
  useCreateExamSessionMutation,
  useBuildExamSessionMutation,
  useUpdateExamSessionMutation,
  useUpdateAndPublishExamSessionMutation,
  useUnPublishExamSessionMutation,
  useDeleteExamDayMutation,
  useCreateExamDayMutation,
  useUpdateExamDayMutation,
  useCreateRoomAssignmentMutation,
  useDeleteRoomAssignmentMutation,
  useUpdateExamAndExpertAssignmentsMutation,
  useUpdateExpertAssignmentsMutation,
  usePublishExamResultsMutation,
  useAddExamSessionLocationsMutation,
  useDeleteExamSessionLocationMutation,
  useStartSurveyMutation,
  useSendInvitationMutation,
  useSendResultNotificationsMutation,
  useExportOrdersMutation,
  useSetCandidatePinMutation,
  useSetExpertPinMutation
} = examSessionApi;
